"use client";

import type { FC, ReactNode } from "react";
import { useId } from "react";
import { ArrowUpTrayIcon, DocumentTextIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { cn } from "@/lib/cn";
import { FieldLabel, FieldMessage, type FieldState } from "./field-chrome";
import { useFileDrop } from "./use-file-drop";

type FileDropzoneProps = {
  className?: string;
  label?: ReactNode;
  message?: ReactNode;
  state?: FieldState;
  required?: boolean;
  accept?: string[];
  minSize?: number;
  maxSize?: number;
  value?: File | null;
  defaultValue?: File | null;
  /** The name of an already-uploaded file, shown until a new one is picked — the document
   *  equivalent of `ImageDropzone`'s `previewUrl`, so a saved draft doesn't read as empty. */
  storedFileName?: string | null;
  /** Replaces the "Drop your file here or browse" / "Drop a new file or browse to replace it"
   *  line with one line of the caller's copy, shown alongside an attached filename rather than
   *  instead of it — e.g. read-only messaging where there is nothing to browse or replace. */
  prompt?: ReactNode;
  onValueChange?: (file: File | null) => void;
  disabled?: boolean;
  id?: string;
};

/** "PDF, JPG or PNG" from `["application/pdf", "image/jpeg", "image/png"]`. */
const formatAccept = (accept: string[]): string => {
  const names = [...new Set(accept.map((mime) => (mime.split("/")[1] ?? mime).toUpperCase()))].map((name) =>
    name === "JPEG" ? "JPG" : name,
  );

  if (names.length <= 1) return names[0] ?? "";

  return `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]}`;
};

export const FileDropzone: FC<FileDropzoneProps> = ({
  className,
  label,
  message,
  state = "default",
  required,
  accept,
  minSize,
  maxSize,
  value,
  storedFileName,
  prompt,
  onValueChange,
  disabled,
  id,
}) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const { file, setFile, dragOver, localError, inputProps, dropProps, formatSize } = useFileDrop({
    accept,
    minSize,
    maxSize,
    value,
    onValueChange,
  });

  const effectiveState: FieldState = localError ? "error" : state;
  const effectiveMessage = localError ?? message;
  /* A fresh pick wins over whatever the draft already carries — same rule as `ImageDropzone`. */
  const attachedName = file?.name ?? storedFileName ?? null;

  return (
    <div className="flex w-full flex-col gap-8">
      <FieldLabel htmlFor={inputId} state={effectiveState} required={required}>
        {label}
      </FieldLabel>
      <label
        htmlFor={inputId}
        {...dropProps}
        className={cn(
          "flex w-full flex-col items-center justify-center gap-8 rounded-medium border border-dashed p-50 text-center transition-colors",
          disabled ? "pointer-events-none cursor-not-allowed opacity-50" : "cursor-pointer",
          dragOver
            ? "border-brand-blue bg-brand-blue/5"
            : effectiveState === "error"
              ? "border-brand-red"
              : "border-neutral-mid-grey",
          className,
        )}
      >
        <input id={inputId} disabled={disabled} {...inputProps} />

        {attachedName ? (
          <DocumentTextIcon aria-hidden className="size-40 shrink-0 text-brand-blue" />
        ) : (
          <ArrowUpTrayIcon aria-hidden className="size-40 shrink-0 text-neutral-black" />
        )}

        {attachedName ? (
          <div className="flex max-w-full items-center gap-8">
            <span className="min-w-0 truncate text-16 font-medium text-neutral-black">{attachedName}</span>
            {/* Only a file picked in this session can be taken back — clearing one already stored
                would have to detach it server-side too, which this control can't reach. Replacing
                it by picking another is the way out. */}
            {file && (
              <button
                type="button"
                aria-label={`Remove ${attachedName}`}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setFile(null);
                }}
                className="shrink-0 cursor-pointer rounded-small p-4 text-neutral-grey hover:bg-neutral-black/5 hover:text-neutral-black"
              >
                <XMarkIcon aria-hidden className="size-20" />
              </button>
            )}
          </div>
        ) : prompt ? (
          <span className="text-16 font-medium text-neutral-black">{prompt}</span>
        ) : (
          <span className="text-16 font-medium text-neutral-black">
            Drop your file here or <span className="text-brand-blue">browse</span>
          </span>
        )}

        {prompt && attachedName && <span className="text-16 font-medium text-neutral-black">{prompt}</span>}

        <span className="flex flex-col text-14 text-neutral-grey">
          {!prompt && attachedName && !file && <span>Drop a new file or browse to replace it</span>}
          {accept && accept.length > 0 && <span>Upload a file in {formatAccept(accept)} format</span>}
          {(minSize !== undefined || maxSize !== undefined) && (
            <span>
              {minSize !== undefined && maxSize !== undefined
                ? `File size must be between ${formatSize(minSize)} and ${formatSize(maxSize)}`
                : maxSize !== undefined
                  ? `File size must be under ${formatSize(maxSize)}`
                  : `File size must be at least ${formatSize(minSize!)}`}
            </span>
          )}
        </span>
      </label>
      <FieldMessage state={effectiveState}>{effectiveMessage}</FieldMessage>
    </div>
  );
};
